<script setup>
import { computed, ref, watch } from 'vue';
import ContactFavoriteImoveisAPI from 'dashboard/api/contactFavoriteImoveis';
import { useAlert } from 'dashboard/composables';
import {
  PENDING_INTENT_KEYS,
  saveAnonymousFavoriteIntent,
  savePendingIntent,
} from 'dashboard/composables/lais/usePendingIntent';

const props = defineProps({
  listing: { type: Object, default: () => ({}) },
  contactId: { type: [String, Number], default: null },
  position: { type: String, default: 'pricebox' },
  businessContext: { type: String, default: 'rental' },
  variant: {
    type: String,
    default: 'icon',
    validator: value => ['icon', 'text'].includes(value),
  },
  initiallyFavorited: { type: Boolean, default: false },
  initialFavoriteId: { type: [String, Number], default: null },
});

const emit = defineEmits(['toggle', 'pending']);

const isLoading = ref(false);
const isFavorited = ref(props.initiallyFavorited);
const favoriteId = ref(props.initialFavoriteId);
const justChanged = ref(false);

const listingId = computed(() => props.listing?.id || props.listing?.imovelId);
const pressedLabel = computed(() =>
  isFavorited.value ? 'Remover dos favoritos' : 'Salvar nos favoritos'
);
const helperTitle = computed(() =>
  isFavorited.value
    ? 'Remover dos favoritos'
    : 'Que tal salvar este imóvel? Crie listas dos seus imóveis preferidos.'
);

watch(
  () => [props.initiallyFavorited, props.initialFavoriteId],
  ([favorited, id]) => {
    isFavorited.value = Boolean(favorited);
    favoriteId.value = id;
  }
);

const markSuccess = () => {
  justChanged.value = true;
  window.setTimeout(() => {
    justChanged.value = false;
  }, 300);
};

const emitToggle = () => {
  emit('toggle', {
    listingId: listingId.value,
    status: isFavorited.value,
    favoriteId: favoriteId.value,
    position: props.position,
    businessContext: props.businessContext,
  });
};

const savePendingFavorite = () => {
  const intent = savePendingIntent(PENDING_INTENT_KEYS.favorite, {
    listingId: listingId.value,
    position: props.position,
    businessContext: props.businessContext,
  });
  saveAnonymousFavoriteIntent({
    imovelId: listingId.value,
    listingId: listingId.value,
    listName: 'default',
    finalidade: props.businessContext,
    position: props.position,
    createdAt: new Date().toISOString(),
  });
  emit('pending', intent);
  useAlert('Abra este imóvel a partir de um cliente para salvar o favorito.');
};

const toggle = async () => {
  if (isLoading.value || !listingId.value) return;
  if (!props.contactId) {
    savePendingFavorite();
    return;
  }

  const previousState = isFavorited.value;
  const previousId = favoriteId.value;
  isLoading.value = true;
  isFavorited.value = !isFavorited.value;

  try {
    if (previousState && previousId) {
      await ContactFavoriteImoveisAPI.delete(props.contactId, previousId);
      favoriteId.value = null;
    } else {
      const { data } = await ContactFavoriteImoveisAPI.create(props.contactId, {
        imovel_id: listingId.value,
        list_name: 'default',
      });
      favoriteId.value = data?.id || data?.favorite?.id || null;
    }
    markSuccess();
    emitToggle();
  } catch {
    isFavorited.value = previousState;
    favoriteId.value = previousId;
    useAlert('Não foi possível salvar o favorito.');
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <button
    type="button"
    class="inline-flex items-center justify-center gap-2 rounded-lais border border-lais-border bg-white font-semibold text-lais-text transition hover:border-lais-primary hover:bg-lais-primary-50 disabled:cursor-not-allowed disabled:opacity-60"
    :class="[
      variant === 'text' ? 'h-9 px-3 text-[13px]' : 'size-9',
      justChanged ? 'scale-105' : '',
    ]"
    :aria-label="pressedLabel"
    :aria-pressed="isFavorited"
    :title="helperTitle"
    :disabled="isLoading || !listingId"
    @click.stop="toggle"
  >
    <span
      class="size-4"
      :class="
        isLoading
          ? 'i-lucide-loader-circle animate-spin'
          : isFavorited
            ? 'i-lucide-heart fill-[#d4546a] text-[#d4546a]'
            : 'i-lucide-heart'
      "
      aria-hidden="true"
    />
    <span v-if="variant === 'text'">{{ pressedLabel }}</span>
  </button>
</template>
